/**
 * Gear rail search — pure, no DOM, no catalogue-loading concerns (that's
 * `useGearCatalog` on the web side). See docs/plans/2026-09-14-av-gear-ui-
 * brief.md §"List view" for the exact contract this implements.
 */
import { SPEAKER_TYPES, type Product, type ProductType } from './schema';

/** The rail's four group chips. */
export type CatalogGroup = 'speakers' | 'subs' | 'receivers' | 'amps';

const GROUP_TYPES: Record<CatalogGroup, readonly ProductType[]> = {
  speakers: SPEAKER_TYPES,
  subs: ['subwoofer'],
  receivers: ['av_receiver', 'av_processor'],
  amps: ['power_amp'],
};

export function productMatchesGroup(product: Pick<Product, 'type'>, group: CatalogGroup): boolean {
  return (GROUP_TYPES[group] as readonly string[]).includes(product.type);
}

/**
 * Human type label, for search matching only. MUST stay byte-identical to
 * `web/src/components/room/gear/labels.ts`'s `TYPE_LABELS` (and to the table
 * in the UI brief) — duplicated rather than imported because `av-catalog` is
 * a dependency-free leaf package and must not reach into `web`.
 */
const TYPE_LABEL: Record<ProductType, string> = {
  speaker_bookshelf: 'Bookshelf',
  speaker_tower: 'Tower',
  speaker_center: 'Center',
  speaker_surround: 'Surround',
  speaker_height: 'Height',
  speaker_in_ceiling: 'In-ceiling',
  subwoofer: 'Subwoofer',
  av_receiver: 'Receiver',
  av_processor: 'Processor',
  power_amp: 'Power amp',
  tv: 'TV',
  projector: 'Projector',
  projector_screen: 'Screen',
};

/** Lowercase, letters/digits only — the "compact form" the brief specifies. */
function compact(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Lowercase words, split on anything that isn't a letter or digit. */
function words(s: string): string[] {
  return s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

function searchableWords(p: Product): string[] {
  return [
    ...words(p.brand),
    ...words(p.model),
    ...(p.series ? words(p.series) : []),
    ...p.aliases.flatMap(words),
    ...words(TYPE_LABEL[p.type]),
  ];
}

/** Every query token must prefix-match at least one word of the product's
 *  searchable text (brand, model, series, aliases, type label). */
function matchesByTokenPrefix(p: Product, queryTokens: readonly string[]): boolean {
  if (queryTokens.length === 0) return true;
  const haystack = searchableWords(p);
  return queryTokens.every((t) => haystack.some((w) => w.startsWith(t)));
}

/** Fallback for a query with no separators (`rp600m`, `x3800`): the compact
 *  query must be a substring of the compact brand+model, or of a compact
 *  alias — catches matches split across hyphens/spaces that token-prefix
 *  matching (which only ever matches a WHOLE word) can't, e.g. "rp600m"
 *  inside "RP-600M II" (words: "rp", "600m", "ii" — no single word starts
 *  with "rp600m"). */
function matchesByCompactSubstring(p: Product, compactQuery: string): boolean {
  if (!compactQuery) return true;
  if (compact(p.brand + p.model).includes(compactQuery)) return true;
  return p.aliases.some((a) => compact(a).includes(compactQuery));
}

function matches(p: Product, queryTokens: readonly string[], compactQuery: string): boolean {
  if (queryTokens.length === 0 && !compactQuery) return true;
  return matchesByTokenPrefix(p, queryTokens) || matchesByCompactSubstring(p, compactQuery);
}

/** Rank tier: 0 = compact model equals the query, 1 = model starts with it,
 *  2 = everything else that matched. Lower sorts first. */
function rankTier(p: Product, compactQuery: string): 0 | 1 | 2 {
  if (!compactQuery) return 2;
  const compactModel = compact(p.model);
  if (compactModel === compactQuery) return 0;
  if (compactModel.startsWith(compactQuery)) return 1;
  return 2;
}

/**
 * Search the catalogue. `query` may be empty (returns every product in
 * `group`, alphabetically). `group` narrows to one rail chip; `null` means
 * every type. Pure and deterministic — same inputs, same order, always.
 */
export function searchCatalog(
  products: readonly Product[],
  query: string,
  group: CatalogGroup | null,
): Product[] {
  const trimmed = query.trim();
  const queryTokens = trimmed ? words(trimmed) : [];
  const compactQuery = compact(trimmed);

  return products
    .filter((p) => (group ? productMatchesGroup(p, group) : true))
    .filter((p) => matches(p, queryTokens, compactQuery))
    .map((p) => ({ p, tier: rankTier(p, compactQuery) }))
    .sort((a, b) => {
      if (a.tier !== b.tier) return a.tier - b.tier;
      const brand = a.p.brand.localeCompare(b.p.brand);
      if (brand !== 0) return brand;
      return a.p.model.localeCompare(b.p.model);
    })
    .map(({ p }) => p);
}
